<template>
  <el-dialog
    class="dialog-wrapper expected-values"
    visible
    :show-close="false"
    @close="abortInsert"
  >
    <ContentHeader
      slot="title"
      :title="$t('analysesData.expectedValues.title')"
    />
    <ValidationObserver
      ref="expectedValuesForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('analysesData.expectedValues.startDate')"
            >
              <el-date-picker
                v-model="formData.startDate"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
                :picker-options="{ disabledDate: isDateDisabled }"
              />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('analysesData.expectedValues.endDate')"
            >
              <el-date-picker
                v-model="formData.endDate"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
                :disabled="!formData.startDate"
                :picker-options="{ disabledDate: isEndDateDisabled }"
              />
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
          v-if="!apiCallStarted"
        >
          <el-col :span="24">
            <div class="table-wrapper">
              <el-table
                v-loading="loading"
                element-loading-background="rgba(48,53,82,0.9)"
                class="width-100"
                :data="sampleAnalyses"
                border
                @selection-change="handleSelectionChange"
                :row-class-name="tableRowClassName"
              >
                <el-table-column
                  type="selection"
                  width="40"
                />
                <el-table-column
                  prop="analysisCode"
                  :label="$t('filters.analysisCode')"
                />
                <el-table-column
                  prop="analysisDescription"
                  :label="$t('filters.analysisDescription')"
                />
                <el-table-column :label="$t('filters.overrideValues')">
                  <template slot-scope="scope">
                    <span>
                      {{ scope.row.valuesCount }}
                    </span>
                  </template>
                </el-table-column>

                <el-table-column
                  prop="samplesCount"
                  :label="$t('filters.samplesCount')"
                />
              </el-table>
            </div>
          </el-col>
        </el-row>
        <BulkCall
          ref="copyOperationProgress"
          :function="analysesDataApicopyOperationValues"
          :arguments="copyOperationValuesArguments"
          @completed="bulkCompleted"
        />

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortInsert"
            v-if="!apiCallStarted"
          >
            {{ $t('global.cancel') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(insertValues)"
            v-if="!apiCallStarted"
          >
            {{ $t('global.confirm') }}
          </el-button>
          <el-button
            v-if="apiCallStarted && apiCallEnded"
            type="primary"
            @click.prevent="closeModal"
          >
            {{ $t('global.close') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import moment from 'moment';

  import { analysesDataApi } from '@/api';

  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import BulkCall from '@/components/BulkCall.vue';

  import groupBy from 'lodash.groupby';
  import uniq from 'lodash.uniq';

  const initialData = {
    groupingId: null,
    startDate: '',
    endDate: '',
    selectedAnalyses: [],
  };

  export default {
    name: 'InsertExpectedValues',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      BulkCall,
    },
    props: {
      samples: { type: Array, required: true },
    },
    data() {
      return {
        formData: { ...initialData },
        sampleAnalyses: [],
        loading: false,
        apiCallStarted: false,
        apiCallEnded: false,
      };
    },
    computed: {
      isBulk() {
        return this.samples.length > 1;
      },
      samplesIds() {
        return this.samples.map((sample) => sample.id);
      },
      maxAcceptedAt() {
        return this.samples.reduce((acc, sample) => {
          if (sample.acceptedAt) {
            const current = moment(sample.acceptedAt).startOf('day').toDate();
            if (!acc) {
              return current;
            }
            return current > acc ? current : acc;
          }
          return acc;
        }, undefined);
      },
      analysesDataApicopyOperationValues() {
        return analysesDataApi.copyOperationValue;
      },
      copyOperationValuesArguments() {
        const dateStart = moment
          .utc(this.formData.startDate)
          .startOf('day')
          .toDate();
        const dateEnd = moment
          .utc(this.formData.endDate)
          .startOf('day')
          .toDate();
        return uniq([...this.formData.selectedAnalyses].flat()).map((item) => [
          item,
          { dateStart, dateEnd },
        ]);
      },
    },
    methods: {
      async insertValues() {
        this.apiCallStarted = true;
        this.$refs.copyOperationProgress.run();
      },
      closeModal() {
        this.$emit('dialogClose', { success: true });
      },
      abortInsert() {
        this.$emit('dialogClose', { success: false });
      },
      handleSelectionChange(selection) {
        const sampleAnalysesIds = selection.map((a) => a.ids);
        this.formData.selectedAnalyses = sampleAnalysesIds;
      },
      isDateDisabled(date) {
        const today = moment.utc().startOf('day').toDate();
        if (date > today) return true;

        if (date < this.maxAcceptedAt) return true;

        return false;
      },
      isEndDateDisabled(date) {
        const today = moment.utc().startOf('day').toDate();
        if (date > today) return true;

        const startDate = moment(this.formData.startDate)
          .startOf('day')
          .toDate();
        if (date < startDate) return true;

        return false;
      },
      tableRowClassName({ row }) {
        if (row.hasValues) return 'warning-row';
        return '';
      },
      bulkCompleted(errors) {
        this.apiCallEnded = true;
        if (!errors) {
          this.closeModal();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        }
      },
    },
    async created() {
      this.loading = true;
      try {
        const sampleAnalysesResponse =
          await analysesDataApi.samplesAnalysesNewIndex(this.samplesIds);
        // group by analysis id
        const grouped = groupBy(
          sampleAnalysesResponse.data,
          (item) =>
            `${item.analysisId}_${item.values ? item.values.length > 0 : false}`
        );
        // create analysis list
        const processed = Object.keys(grouped)
          .sort()
          .map((key) => {
            const sampleAnalysesList = grouped[key];
            const sampleAnalysis = sampleAnalysesList[0];
            const ids = sampleAnalysesList.map((a) => a.labSampleAnalysisId);
            if (sampleAnalysis.values) {
              this.formData.startDate = sampleAnalysesList
                .flatMap((sa) => sa.values.map((val) => val.analysisEnd))
                .reduce(
                  (acc, elem) => (acc && acc < elem ? acc : elem),
                  undefined
                );
            }
            const endedValues =
              sampleAnalysis.values?.filter((value) => {
                return value.printOnWorksheet && value.analysisEnd != null;
              }) ?? [];
            return {
              analysisCode: sampleAnalysis.analysisCode,
              analysisDescription: sampleAnalysis.analysisDescription,
              samplesCount: sampleAnalysesList.length,
              hasValues: endedValues.length > 0,
              valuesCount: endedValues.length,
              ids,
              list: sampleAnalysesList,
            };
          });
        this.sampleAnalyses = processed;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      }
    },
  };
</script>

<style lang="scss">
  .expected-values {
    .el-dialog {
      width: 60%;
    }
    .table-wrapper .el-table .el-table__row.warning-row {
      background-color: $--color-warning;
    }
  }
</style>
